import { useEffect, useState } from "react";

import { buildSilentAuthUrl, isCognitoConfigured } from "../services/cognito.service";

// All routes that unauthenticated users are allowed to visit directly.
// If a user lands on any of these, we must NOT trigger the silent Cognito
// redirect — it would steal them away from the page they intended to reach.
const PUBLIC_ROUTES = [
	"/callback",
	"/login",
	"/sign-up",
	"/forgot-password",
	"/reset-password",
	"/error",
	"/faq",
	"/contact-us",
];

// These routes have dynamic segments (e.g. /guest-dashboard-access/abc123)
// so we check the path prefix instead of an exact match.
const PUBLIC_ROUTE_PREFIXES = ["/guest-dashboard-access/", "/guest-dashboard-entry/"];

// Returns true if the current page is a public route that should never
// trigger a silent Cognito redirect.
const isPublicRoute = () => {
	const path = window.location.pathname;

	if (PUBLIC_ROUTES.includes(path)) {
		return true;
	}

	return PUBLIC_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
};

// This hook silently checks if the user already has an active Cognito
// session when they land on the app with no local Bluechip session.
// If Cognito finds a session, it redirects back to /callback and the
// user gets logged in without ever seeing a login form.
// If Cognito finds no session, it immediately returns an error to /callback
// and the user lands on the login page as normal.
const useSilentCognitoAuth = (hasToken, hasUser) => {
	const [isCheckingCognito, setIsCheckingCognito] = useState(false);

	useEffect(() => {
		const isAlreadyLoggedIn = hasToken && hasUser;

		// Skip the silent check if:
		// - User already has a local Bluechip session
		// - The current page is a public route (login, reset password, sign up, etc.)
		// - Cognito is not configured yet
		if (isAlreadyLoggedIn || isPublicRoute() || !isCognitoConfigured()) {
			return;
		}

		// No local session and Cognito is configured —
		// silently check if the user has an active Cognito session
		const silentAuthUrl = buildSilentAuthUrl();
		if (silentAuthUrl) {
			// Save where the user was trying to go, plus the current time so
			// CognitoCallback can reject it if too much time has passed
			localStorage.setItem(
				"cognito_intended_url",
				JSON.stringify({
					url: window.location.pathname,
					savedAt: Date.now(),
				}),
			);
			setIsCheckingCognito(true);
			window.location.href = silentAuthUrl;
		}
	}, [hasToken, hasUser]);

	return { isCheckingCognito };
};

export default useSilentCognitoAuth;
